import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Mail, ShieldAlert } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { Field } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { BRAND } from "@/lib/brand";

const TOPICS = ["General Question", "Account Help", "Reminder Help", "Import/Export", "Document Upload", "Billing",
  "Privacy / Deletion", "Feedback / Feature Request", "Bug Report", "Other"];
const EMPTY = { name: "", email: "", topic: "", subject: "", message: "", consent: false, website: "" };

const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2 || f.name.length > 100) e.name = "Please enter 2–100 characters";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Please enter a valid email address";
  if (!f.topic) e.topic = "Please choose a topic";
  if (f.subject.trim().length < 5 || f.subject.length > 150) e.subject = "Please enter 5–150 characters";
  if (f.message.trim().length < 20 || f.message.length > 5000) e.message = "Please enter 20–5000 characters";
  if (!f.consent) e.consent = "Please acknowledge to continue";
  return e;
};

export default function Contact() {
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [fail, setFail] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(f);
    setErrors(v);
    setFail("");
    if (Object.keys(v).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/contact", f);
      setDone(data);
      setF(EMPTY);
    } catch (err) {
      setFail(errMsg(err, "Your message could not be sent."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout>
      <section className="max-w-6xl mx-auto px-5 lg:px-10 pt-14 grid lg:grid-cols-[1fr_1.4fr] gap-10" data-testid="contact-page">
        <div className="pm-fade-up">
          <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#A0136E]">Support</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold mt-2">Get in Touch</h1>
          <p className="text-[var(--pm-muted)] mt-4 leading-relaxed">Have a question about reminders, importing Excel files, attaching documents, account access or a new feature idea? Send us a message using the form.</p>
          <div className="pm-card p-5 mt-8 flex gap-3 items-center">
            <Mail className="w-5 h-5 text-[var(--pm-primary)]" />
            <div><p className="text-xs text-[var(--pm-muted)]">Prefer email?</p><a href={`mailto:${BRAND.email}`} className="font-bold" data-testid="contact-email-link">{BRAND.email}</a></div>
          </div>
          <div className="rounded-2xl bg-[#FEF3E2] text-[#7A4705] p-5 mt-4 flex gap-3 text-sm">
            <ShieldAlert className="w-5 h-5 shrink-0" />
            <p>Please do not send passwords, full payment-card numbers, passport scans or other sensitive files through the support form.</p>
          </div>
        </div>
        <div className="pm-card p-6 sm:p-9 pm-fade-up" style={{ animationDelay: ".08s" }}>
          {done ? (
            <div className="text-center py-10" data-testid="contact-success">
              <CheckCircle2 className="w-14 h-14 mx-auto text-[var(--pm-success)]" />
              <h2 className="text-2xl font-extrabold mt-4">Message received</h2>
              <p className="text-[var(--pm-muted)] mt-2">Your request ID is <strong className="text-[var(--pm-ink)]" data-testid="contact-ticket-id">{done.ticket_id}</strong>. Keep it for reference.</p>
              {!done.email_forwarded && <p className="text-xs text-[var(--pm-muted)] mt-3">Your message is saved. If it's urgent, you can also email {BRAND.email}.</p>}
              <button className="pm-btn pm-btn-outline mt-6" onClick={() => setDone(null)} data-testid="contact-send-another">Send another message</button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="grid sm:grid-cols-2 gap-5" data-testid="contact-form">
              <Field label="Your name" required error={errors.name} htmlFor="c-name"><input id="c-name" className="pm-input" value={f.name} onChange={set("name")} aria-invalid={!!errors.name} data-testid="contact-name-input" /></Field>
              <Field label="Email address" required error={errors.email} htmlFor="c-email"><input id="c-email" type="email" className="pm-input" value={f.email} onChange={set("email")} aria-invalid={!!errors.email} data-testid="contact-email-input" /></Field>
              <div className="sm:col-span-2"><Field label="Topic" required error={errors.topic} htmlFor="c-topic">
                <select id="c-topic" className="pm-input" value={f.topic} onChange={set("topic")} aria-invalid={!!errors.topic} data-testid="contact-topic-select">
                  <option value="">Choose a topic…</option>{TOPICS.map((t) => <option key={t}>{t}</option>)}
                </select></Field></div>
              <div className="sm:col-span-2"><Field label="Subject" required error={errors.subject} htmlFor="c-subject"><input id="c-subject" className="pm-input" value={f.subject} onChange={set("subject")} aria-invalid={!!errors.subject} data-testid="contact-subject-input" /></Field></div>
              <div className="sm:col-span-2"><Field label="Message" required error={errors.message} hint={`${f.message.length}/5000`} htmlFor="c-msg"><textarea id="c-msg" rows={6} className="pm-input" value={f.message} onChange={set("message")} aria-invalid={!!errors.message} data-testid="contact-message-input" /></Field></div>
              <input type="text" name="website" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <div className="sm:col-span-2">
                <label className="flex gap-3 items-start text-sm cursor-pointer">
                  <input type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-1 w-4 h-4 accent-[var(--pm-primary)]" data-testid="contact-consent-checkbox" />
                  <span>I understand this information will be used to respond to my request. See the <Link to="/privacy-policy" className="text-[var(--pm-primary)] font-semibold">Privacy Policy</Link>.</span>
                </label>
                {errors.consent && <p className="text-xs text-[var(--pm-danger)] mt-1">{errors.consent}</p>}
              </div>
              {fail && <p className="sm:col-span-2 text-sm text-[var(--pm-danger)] bg-[#FDECEE] p-3 rounded-xl" role="alert" data-testid="contact-error">{fail} You can also email {BRAND.email}.</p>}
              <div className="sm:col-span-2"><button type="submit" disabled={busy} className="pm-btn pm-btn-primary w-full sm:w-auto h-12 px-8" data-testid="contact-submit-btn">{busy ? "Sending…" : "Send message"}</button></div>
            </form>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
